import { useState } from 'react'

import {
  AppShell,
  Badge,
  Box,
  Center,
  Divider,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core'
import {
  type IconHome2,
  IconLogout,
  IconMoonStars,
  IconSun,
  IconUser,
} from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'

import { useLogout } from '@repo/common/queries/auth'

import { useColorScheme } from '@/app/AppContexts'
import { Logo } from '@/components/Logo'
import { useDynamicMenuItems } from '@/hooks/useDynamicMenuItems'

import classes from './Navbar.module.css'

type NavbarLinkProps = {
  icon?: typeof IconHome2
  label: string
  active?: boolean
  isParentActive?: boolean
  disabled?: boolean
  onClick?: () => void
  to?: string
  isInnerItem?: boolean
  notificationCount?: number
  tooltipDisabled?: boolean
}

function NavbarLink({
  icon: Icon,
  label,
  active,
  isParentActive,
  onClick,
  to,
  isInnerItem = false,
  disabled = false,
  notificationCount,
  tooltipDisabled = false,
}: NavbarLinkProps) {
  const linkClassName = `${classes.link} ${isInnerItem ? classes.inner_link : ''}`

  const linkElement = (
    <Link
      to={to || ''}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        if (!to) {
          e.preventDefault()
        }

        if (!disabled && onClick) {
          onClick()
        }
      }}
      className={linkClassName}
      data-active={active || undefined}
      data-parent-active={isParentActive || undefined}
      data-disabled={disabled || undefined}
      disabled={disabled || undefined}
    >
      {Icon && <Icon size={20} stroke={1.5} />}
      {isInnerItem && (
        <Text mt="xxxs" className={classes.inner_link_text}>
          {label}
        </Text>
      )}
    </Link>
  )

  if (tooltipDisabled) {
    return linkElement
  }

  return (
    <Tooltip label={label} position="right">
      <Box style={{ position: 'relative', overflow: 'visible' }}>
        {linkElement}

        {notificationCount && notificationCount > 0 && (
          <Badge
            color="red"
            size="xs"
            variant="filled"
            radius="xl"
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              padding: '0 4px',
              minWidth: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
              zIndex: 2,
              transform: 'translateX(-2px) translateY(2px)',
            }}
          >
            {notificationCount > 9 ? '9+' : notificationCount}
          </Badge>
        )}
      </Box>
    </Tooltip>
  )
}

export function Navbar() {
  const logout = useLogout()
  const { colorScheme, toggleColorScheme } = useColorScheme()
  const { main: menuItems, secondary: secondaryItems } = useDynamicMenuItems()
  const [unreadNotificationCount] = useState<number>(0)

  const links = menuItems.map((item) => {
    const showNotificationCount =
      item.label === 'Notifications' && unreadNotificationCount > 0

    return (
      <NavbarLink
        key={item.label}
        icon={item.icon}
        label={item.label}
        active={item.active}
        isParentActive={false}
        to={item.to}
        disabled={item.disabled}
        notificationCount={
          showNotificationCount ? unreadNotificationCount : undefined
        }
      />
    )
  })

  const innerLinks = secondaryItems.map((item, idx) => {
    if (item.separator) {
      return <Divider key={`sep-${idx}`} my="xs" />
    }

    if (!item.icon) {
      return (
        <Text className={classes.inner_title} key={item.label} size="sm">
          {item.label}
        </Text>
      )
    }

    return (
      <NavbarLink
        key={item.label}
        icon={item.icon}
        label={item.label}
        active={item.active}
        to={item.to}
        disabled={item.disabled}
        isInnerItem={true}
        tooltipDisabled={true}
      />
    )
  })

  return (
    <AppShell.Navbar className={classes.navbar}>
      <AppShell.Section className={classes.top_section}>
        <Center>
          <Logo size={30} className={classes.logo} />
        </Center>
      </AppShell.Section>

      <Box className={classes.content_container}>
        <Box className={classes.main_section}>
          <AppShell.Section grow>
            <Stack justify="center" gap={0}>
              {links}
            </Stack>
          </AppShell.Section>
          <AppShell.Section>
            <Stack justify="center" gap={0}>
              <NavbarLink
                icon={colorScheme === 'dark' ? IconSun : IconMoonStars}
                label={colorScheme === 'dark' ? 'Light mode' : 'Dark mode'}
                onClick={toggleColorScheme}
              />
              <NavbarLink icon={IconUser} label="Profile" to="/profile" />
              <NavbarLink
                icon={IconLogout}
                label="Logout"
                onClick={() => {
                  logout.mutate()
                }}
              />
            </Stack>
          </AppShell.Section>
        </Box>
        <Box className={classes.inner_section}>
          <AppShell.Section grow>
            <Stack justify="flex-start" gap={0}>
              {innerLinks}
            </Stack>
          </AppShell.Section>
        </Box>
      </Box>
    </AppShell.Navbar>
  )
}
